import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, withCtx as _withCtx, createVNode as _createVNode, unref as _unref } from "vue"

const _hoisted_1 = { class: "w-full flex flex-col items-center" }
const _hoisted_2 = {
  key: 0,
  class: "w-full bg-white pt-4 pb-2 mb-4 overflow-hidden"
}
const _hoisted_3 = { class: "px-6 mb-2" }
const _hoisted_4 = { class: "text-[9px] uppercase tracking-widest text-gray-900" }
const _hoisted_5 = { class: "flex overflow-x-auto no-scrollbar px-6 gap-3" }
const _hoisted_6 = ["onClick"]
const _hoisted_7 = {
  key: 1,
  class: "w-full h-full relative"
}
const _hoisted_8 = ["src", "alt"]
const _hoisted_9 = ["src", "alt"]
const _hoisted_10 = {
  key: 1,
  class: "w-full bg-white pt-4 pb-2 border-b border-gray-100 mb-4 overflow-hidden"
}
const _hoisted_11 = { class: "px-6 mb-4" }
const _hoisted_12 = { class: "text-xs font-bold uppercase tracking-widest text-gray-900" }
const _hoisted_13 = { class: "flex overflow-x-auto no-scrollbar snap-x snap-proximity px-6 gap-6" }
const _hoisted_14 = ["onClick"]
const _hoisted_15 = {
  key: 1,
  class: "w-full h-full relative"
}
const _hoisted_16 = ["src", "alt"]
const _hoisted_17 = ["src", "alt"]
const _hoisted_18 = { class: "relative w-full flex justify-center m-0 p-0 lg:p-0 top-0 h-full bg-white" }
const _hoisted_19 = {
  key: 0,
  class: "w-1/4 pl-4 max-w-[400px] sticky top-20 h-fit"
}
const _hoisted_20 = {
  key: 0,
  class: "flex flex-col justify-center"
}
const _hoisted_21 = { class: "flex-grow text-base tracking-tight text-gray-900 sm:text-2xl sm:pr-14 font-ivymode" }

import {computed, onMounted, onUnmounted, ref} from 'vue'
import {filterListType} from "@/assets/ts/types/filters";
import {FilterLimiter} from "@/assets/ts/FilterLimiter";
import {i18n} from "@/main";
import {useI18n} from "vue-i18n";
import CatalogMenu from "@/components/catalog/catalogMenu.vue";
import CatalogMenuSkeleton from "@/components/catalog/catalogMenuSkeleton.vue";
import CatalogResult from "@/components/catalog/catalogResult.vue";
import StepWidget from "@/components/stepWidget.vue";
import ElementVideo from "@/components/elements/elementVideo.vue";
import {Config} from "@/assets/ts/config";

interface Product {
  set_id: string;
  hash?: string;
  price: number;
  price_gold: number;
  price_platinum: number;
  metal: string;
  stones: string;
  image: {
    filename: string
    filename_layer: string
    positions: {
      filename: string
      filename_layer: string
    }[]
  };
  order_idx?: number;
}

const pageStr = "components.catalog"


export default /*@__PURE__*/_defineComponent({
  __name: 'catalogMain',
  setup(__props) {

const mobileFiltersOpen = ref(false)
const isMobile = ref(false)

const checkMobile = () => {
  isMobile.value = window.innerWidth < 1024;
};

const {t} = useI18n();

const filterLimiter = new FilterLimiter();

/**
 * catalog props
 */

let catalogBrand = Config.getCatalogBrand() || "R"
const catalogItemType = ref(Config.getCatalogItemType() || "engagement_ring")

const catalogItemTypes = [
  "wedding_ring",
  "engagement_ring",
  "engagement_ring_rings_of_the_stars",
  "eternity_ring",
  "earring",
  "necklace",
  "jewelry_sets",
  "tennis"
]

const mergedFilters = computed(() => {
  // If runtimeConfig catalogBrand is "R" remove the "wedding_ring" from catalogItemTypes
  let finalCatalogItemTypes = catalogItemTypes
  if (catalogBrand === "R") {
    finalCatalogItemTypes.splice(catalogItemTypes.indexOf("wedding_ring"), 1);
  }
  //

  const result: filterListType = {
    catalogItemType: {
      order: -1,
      prio: true,
      values: finalCatalogItemTypes,
      lowercaseImage: false
    },
    price_sort: {
      order: (filters.value['price']?.order || 2) + 0.1,
      prio: false,
      values: ['default', 'min-price', 'max-stone-size', 'max-quality'],
      lowercaseImage: false
    }
  }

  return {...result, ...filters.value}
})

const locale = (i18n.global.locale as any).value || "de-DE";

let [language, country] = locale.split("-");

const products = ref<Product[]>([]);
const failedProducts = ref<Product[]>([]);
const filters = ref<filterListType>({});
const activeFilters = ref<{ [key: string]: string }>({});
const lastChangedFilter = ref<string | null>(null)
const loading = ref(true);
const error = ref<string | null>(null);
const hash = ref("")
const possibleImagePositions = ref<string[]>([])
const fixImagePosition = ref<string>("")

const sliderProducts = computed(() => {
  const isPriceFilterActive = !!activeFilters.value.price
  const allItems = [...products.value, ...failedProducts.value].sort((a, b) => {
    if (isPriceFilterActive) {
      return (a.price ?? 0) - (b.price ?? 0)
    }
    return (a.order_idx ?? 0) - (b.order_idx ?? 0)
  })
  if (allItems.length === 0) return []
  const firstProduct = allItems[0]
  const base = firstProduct.image ? [{
    ...firstProduct.image,
    position: (firstProduct.image.filename.includes('video') || firstProduct.image.filename.endsWith('.mp4')) ? 'video' : 'main'
  }] : []
  const positions = firstProduct.image?.positions.map((p, idx) => {
    // try to match position name from filename if possible, otherwise use index
    let pos = `pos${idx + 2}`
    if (p.filename.includes('model')) pos = 'model'
    else if (p.filename.includes('closeup')) pos = 'closeup'
    else if (p.filename.includes('still')) pos = 'still'
    else if (p.filename.includes('video') || p.filename.endsWith('.mp4')) pos = 'video'
    else if (p.filename.includes('pos2')) pos = 'pos2'
    else if (p.filename.includes('pos3')) pos = 'pos3'
    else if (p.filename.includes('cross_sections')) pos = 'cross_sections'
    else if (p.filename.includes('single1')) pos = 'single1'
    else if (p.filename.includes('single2')) pos = 'single2'
    else if (p.filename.includes('set')) pos = 'set'

    return {...p, position: pos}
  }) ?? []

  // Ensure unique positions (sometimes the API might have duplicates or weird data)
  const all = [...base, ...positions]
  const seen = new Set()
  const unique = all.filter(item => {
    if (catalogItemType.value == "wedding_ring" && item.position == "closeup" && seen.has(item.position)) {
      item.position = "closeup-2"
    } else {
      if (seen.has(item.position)) return false
    }

    seen.add(item.position)
    return true
  })

  // Sort: primary 1st, pos* 3rd and 5th, middle images in between
  const first = unique[0]
  const isPrimary = first && (first.position === 'main' || first.position === 'video')

  const sorted = []
  if (isPrimary) sorted.push(first)

  const remaining = isPrimary ? unique.slice(1) : unique

  const posImages = remaining.filter(i => i.position.startsWith('pos'))
  const middleImages = remaining.filter(i => ['model', 'closeup', 'closeup-2', 'still'].includes(i.position))
  const rest = remaining.filter(i => !i.position.startsWith('pos') && !['model', 'closeup', 'closeup-2', 'still'].includes(i.position))

  let mIdx = 0
  let pIdx = 0

  // 2nd position: middle
  if (middleImages[mIdx]) sorted.push(middleImages[mIdx++])
  // 3rd position: pos
  if (posImages[pIdx]) sorted.push(posImages[pIdx++])
  // 4th position: middle
  if (middleImages[mIdx]) sorted.push(middleImages[mIdx++])
  // 5th position: pos
  if (posImages[pIdx]) sorted.push(posImages[pIdx++])

  // Remaining middle images
  while (mIdx < middleImages.length) sorted.push(middleImages[mIdx++])
  // Remaining pos images
  while (pIdx < posImages.length) sorted.push(posImages[pIdx++])
  // Everything else (video, etc.)
  sorted.push(...rest)

  return sorted
})


const updateUrlHash = () => {
  const newHash = "/" + Object.entries(activeFilters.value)
      .filter(([key, value]) => !(key === 'stone_type' && value === 'earth'))
      .map(([key, value]) => `${key}/${value}`)
      .join('/');

  if (newHash !== hash.value) {
    hash.value = newHash;
    const url = new URL(window.location.href);
    url.hash = hash.value;
    window.history.replaceState({}, '', url.href);
  }
};

const updateFilter = (filterName: string, value: string) => {

  if ((value === "" || (filterName === 'price_sort' && value === 'default')) && filterName !== 'stone_type') {
    removeFilter(filterName)
  } else {
    if (filterName === 'catalogItemType') {
      catalogItemType.value = value
      activeFilters.value = {
        stone_type: catalogItemType.value === 'engagement_ring_rings_of_the_stars' ? "lab" : "earth"
      }
    } else {
      activeFilters.value[filterName] = value === "" && filterName === 'stone_type' ? (catalogItemType.value === 'engagement_ring_rings_of_the_stars' ? "lab" : "earth") : value;

      if (filterName === 'price_sort' && value === 'max-stone-size' && activeFilters.value['price']) {
        activeFilters.value['stone_type'] = 'lab';
      }

      if (filterName === 'price_sort' && value === 'min-price') {
        activeFilters.value['stone_type'] = 'lab';
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete activeFilters.value['price'];
      }
    }

    activeFilters.value = filterLimiter.checkActiveFilters(activeFilters.value);

    lastChangedFilter.value = filterName
    updateUrlHash();
  }
};

const removeFilter = (filterName: string) => {
  if (filterName === 'catalogItemType') return

  if (filterName === 'stone_type') {
    activeFilters.value['stone_type'] = "earth"
  } else if (activeFilters.value[filterName]) {
    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    delete activeFilters.value[filterName];
  }

  activeFilters.value = filterLimiter.checkActiveFilters(activeFilters.value);

  // Update lastChangedFilter reference
  if (lastChangedFilter.value === filterName) {
    const keys = Object.keys(activeFilters.value)
    lastChangedFilter.value = keys.length ? keys[0] : null
  }
  updateUrlHash();
};

const handleResetFilters = () => {
  activeFilters.value = {
    stone_type: catalogItemType.value === 'engagement_ring_rings_of_the_stars' ? "lab" : "earth"
  }
  lastChangedFilter.value = null
  updateUrlHash();
}

// Initialize active filters from the catalogFilters config (URL hash below takes precedence over these)
Object.entries(Config.getCatalogFilters()).forEach(([key, value]) => {
  if (key === 'catalogItemType') {
    catalogItemType.value = value
  } else {
    activeFilters.value[key] = value
  }
})

hash.value = window.location.hash.slice(1);
if (hash.value.startsWith('/')) {
  hash.value = hash.value.slice(1);
}

if (hash.value) {
  const filterPairs = hash.value.split('/');

  for (let i = 0; i < filterPairs.length; i += 2) {
    if (filterPairs[i] && filterPairs[i + 1]) {
      const idx: string = filterPairs[i] || ''
      activeFilters.value[idx.toString()] = filterPairs[i + 1] ? filterPairs[i + 1]?.toString() : ""
    }
  }
  if (activeFilters.value['catalogItemType']) {
    catalogItemType.value = activeFilters.value['catalogItemType']
    delete activeFilters.value['catalogItemType']
  }

  if (activeFilters.value['price_sort'] === 'max-stone-size' && activeFilters.value['price']) {
    activeFilters.value['stone_type'] = 'lab'
  }

  if (activeFilters.value['price_sort'] === 'min-price') {
    activeFilters.value['stone_type'] = 'lab'
    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    delete activeFilters.value['price']
  }
}

if (!activeFilters.value['stone_type'] || activeFilters.value['stone_type'] === "") {
  activeFilters.value['stone_type'] = catalogItemType.value === 'engagement_ring_rings_of_the_stars' ? "lab" : "earth"
}

activeFilters.value = filterLimiter.checkActiveFilters(activeFilters.value);
updateUrlHash();

// Initialize lastChangedFilter if filters came from URL
if (!lastChangedFilter.value) {
  const keys = Object.keys(activeFilters.value)
  lastChangedFilter.value = keys.length ? keys[keys.length - 1] : null
}

onMounted(() => {
  checkMobile();
  window.addEventListener('resize', checkMobile);
});

onUnmounted(() => {
  window.removeEventListener('resize', checkMobile);
});

const selectImagePosition = (position: string) => {
  fixImagePosition.value = position
}

const toggleFilters = () => {
  mobileFiltersOpen.value = !mobileFiltersOpen.value
}

// Build page title from ALL selected filters
// - order: alphabetically by localized value
// - values: localized using the same keys as catalogFilterValue component
// - separator: |
const numberFilter: Record<string, { decimal: number; unit: string }> = {
  stone_size: {decimal: 2, unit: 'ct.'}
}

const formatNumber = (filter: string, value: string | number): string => {
  const cfg = numberFilter[filter]
  if (!cfg) return String(value)
  const num = Number(value)
  if (Number.isNaN(num)) return String(value)
  const rounded = Number(parseFloat(String(num)).toFixed(cfg.decimal))
  const localized = rounded.toLocaleString('de-DE')
  return cfg.unit ? `${localized} ${cfg.unit}` : localized
}

const selectedFilterValues = computed(() => {
  const entries = Object.entries(activeFilters.value)
  if (!entries.length) return [] as string[]

  const localizedValues = entries.map(([key, val]) => {
    // number formatting (same logic as catalogFilterValue)
    if (Object.prototype.hasOwnProperty.call(numberFilter, key)) {
      return formatNumber(key, val)
    }

    // translate value; fallback to raw when missing
    const translated = t(`components.catalog.filter.${key}.${val}`)
    return translated || String(val)
  })

  // Sort alphabetically (case-insensitive)
  return localizedValues.sort((a, b) => a.localeCompare(b, undefined, {sensitivity: 'base'}))
})

const title = computed(() => {
  const key = `components.catalog.catalogs.${catalogItemType.value}`
  const translated = t(key)
  return translated === key ? catalogItemType.value : translated
})


/**
 * Step Widget
 */

const showStepWidget = computed(() => {
  return ['engagement_ring', 'engagement_ring_rings_of_the_stars', 'necklace'].includes(catalogItemType.value)
})


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(StepWidget, {
      show: showStepWidget.value,
      "catalog-id": catalogItemType.value
    }, {
      default: _withCtx(() => [
        (!isMobile.value && !loading.value && !error.value && sliderProducts.value.length > 0)
          ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
              _createElementVNode("div", _hoisted_3, [
                _createElementVNode("h3", _hoisted_4, _toDisplayString(_ctx.$t('components.catalog.imagePosition.title')), 1)
              ]),
              _createElementVNode("div", _hoisted_5, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sliderProducts.value, (item) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: item.position,
                    class: "flex flex-col items-center flex-shrink-0 cursor-pointer",
                    onClick: ($event: any) => (selectImagePosition(item.position))
                  }, [
                    _createElementVNode("div", {
                      class: _normalizeClass(["w-10 h-10 rounded-full border p-0.5 transition-all flex items-center justify-center overflow-hidden", fixImagePosition.value === item.position ? 'border-[#a99d8b] bg-[#a99d8b]/5' : 'border-gray-200'])
                    }, [
                      (item.filename.endsWith('.mp4'))
                        ? (_openBlock(), _createBlock(ElementVideo, {
                            key: 0,
                            src: item.filename,
                            "video-class": "w-full h-full object-contain rounded-full",
                            autoplay: false,
                            loop: false
                          }, null, 8, ["src"]))
                        : (_openBlock(), _createElementBlock("div", _hoisted_7, [
                            (item.filename_layer)
                              ? (_openBlock(), _createElementBlock("img", {
                                  key: 0,
                                  src: item.filename_layer,
                                  class: "absolute top-0 left-0 w-full h-full object-contain rounded-full",
                                  alt: item.position
                                }, null, 8, _hoisted_8))
                              : _createCommentVNode("", true),
                            _createElementVNode("img", {
                              src: item.filename,
                              class: "w-full h-full object-contain rounded-full",
                              alt: item.position
                            }, null, 8, _hoisted_9)
                          ]))
                    ], 2)
                  ], 8, _hoisted_6))
                }), 128))
              ])
            ]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }, 8, ["show", "catalog-id"]),
    (isMobile.value)
      ? (_openBlock(), _createBlock(CatalogMenu, {
          key: 0,
          "mobile-filters-open": mobileFiltersOpen.value,
          "onUpdate:mobileFiltersOpen": _cache[0] || (_cache[0] = ($event: any) => ((mobileFiltersOpen).value = $event)),
          filters: mergedFilters.value,
          "active-filters": {...activeFilters.value, catalogItemType: catalogItemType.value},
          "catalog-item-type": catalogItemType.value,
          onUpdateFilter: updateFilter,
          onRemoveFilter: removeFilter,
          onResetFilters: handleResetFilters
        }, null, 8, ["mobile-filters-open", "filters", "active-filters", "catalog-item-type"]))
      : _createCommentVNode("", true),
    (isMobile.value && !loading.value && !error.value && sliderProducts.value.length > 0)
      ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
          _createElementVNode("div", _hoisted_11, [
            _createElementVNode("h3", _hoisted_12, _toDisplayString(_ctx.$t('components.catalog.imagePosition.title')), 1)
          ]),
          _createElementVNode("div", _hoisted_13, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sliderProducts.value, (item) => {
              return (_openBlock(), _createElementBlock("div", {
                key: item.position,
                class: "flex flex-col items-center flex-shrink-0 snap-start",
                onClick: ($event: any) => (selectImagePosition(item.position))
              }, [
                _createElementVNode("div", {
                  class: _normalizeClass(["w-20 h-20 rounded-full border p-1 mb-2 transition-all flex items-center justify-center overflow-hidden", fixImagePosition.value === item.position ? 'border-[#a99d8b] bg-[#a99d8b]/5' : 'border-gray-200'])
                }, [
                  (item.filename.endsWith('.mp4'))
                    ? (_openBlock(), _createBlock(ElementVideo, {
                        key: 0,
                        src: item.filename,
                        "video-class": "w-full h-full object-contain rounded-full",
                        autoplay: false,
                        loop: false
                      }, null, 8, ["src"]))
                    : (_openBlock(), _createElementBlock("div", _hoisted_15, [
                        (item.filename_layer)
                          ? (_openBlock(), _createElementBlock("img", {
                              key: 0,
                              src: item.filename_layer,
                              class: "absolute top-0 left-0 w-full h-full object-contain rounded-full",
                              alt: item.position
                            }, null, 8, _hoisted_16))
                          : _createCommentVNode("", true),
                        _createElementVNode("img", {
                          src: item.filename,
                          class: "w-full h-full object-contain rounded-full",
                          alt: item.position
                        }, null, 8, _hoisted_17)
                      ]))
                ], 2),
                _createElementVNode("span", {
                  class: _normalizeClass(["text-[11px] uppercase tracking-wider text-center max-w-[80px]", fixImagePosition.value === item.position ? 'text-[#a99d8b] font-bold' : 'text-gray-500'])
                }, _toDisplayString(_ctx.$t(`components.catalog.imagePosition.${item.position}`)), 3)
              ], 8, _hoisted_14))
            }), 128))
          ])
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_18, [
      _createVNode(CatalogResult, {
        products: products.value,
        "onUpdate:products": _cache[1] || (_cache[1] = ($event: any) => ((products).value = $event)),
        failedProducts: failedProducts.value,
        "onUpdate:failedProducts": _cache[2] || (_cache[2] = ($event: any) => ((failedProducts).value = $event)),
        filters: filters.value,
        "onUpdate:filters": _cache[3] || (_cache[3] = ($event: any) => ((filters).value = $event)),
        possibleImagePositions: possibleImagePositions.value,
        "onUpdate:possibleImagePositions": _cache[4] || (_cache[4] = ($event: any) => ((possibleImagePositions).value = $event)),
        loading: loading.value,
        "onUpdate:loading": _cache[5] || (_cache[5] = ($event: any) => ((loading).value = $event)),
        error: error.value,
        "onUpdate:error": _cache[6] || (_cache[6] = ($event: any) => ((error).value = $event)),
        "catalog-brand": _unref(catalogBrand),
        "catalog-item-type": catalogItemType.value,
        "active-filters": activeFilters.value,
        country: _unref(country),
        language: _unref(language),
        "fix-image-position": fixImagePosition.value,
        hash: hash.value,
        onUpdateFilter: updateFilter
      }, null, 8, ["products", "failedProducts", "filters", "possibleImagePositions", "loading", "error", "catalog-brand", "catalog-item-type", "active-filters", "country", "language", "fix-image-position", "hash"]),
      (!isMobile.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_19, [
            false
              ? (_openBlock(), _createElementBlock("div", _hoisted_20, [
                  _createElementVNode("p", _hoisted_21, _toDisplayString(title.value), 1)
                ]))
              : _createCommentVNode("", true),
            (loading.value)
              ? (_openBlock(), _createBlock(CatalogMenuSkeleton, { key: 1 }))
              : _createCommentVNode("", true),
            _createVNode(CatalogMenu, {
              filters: mergedFilters.value,
              "active-filters": {...activeFilters.value, catalogItemType: catalogItemType.value},
              "catalog-item-type": catalogItemType.value,
              onUpdateFilter: updateFilter,
              onRemoveFilter: removeFilter,
              onResetFilters: handleResetFilters
            }, null, 8, ["filters", "active-filters", "catalog-item-type"])
          ]))
        : _createCommentVNode("", true)
    ])
  ]))
}
}

})